import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import * as Icons from '../constants/icons';
import AiPlanBlockRenderer, { AiPlanContent } from './AiPlanBlockRenderer';

export interface AiPlanRecord {
  id: number;
  planned_date: string | null;
  author: string;
  goal: string;
  resources: string | null;
  file_paths: string[] | null;
  content: AiPlanContent;
  conversation_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface AiPlanDetailProps {
  open: boolean;
  plan: AiPlanRecord | null;
  onClose: () => void;
}

const MetaRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

const AiPlanDetail: React.FC<AiPlanDetailProps> = ({ open, plan, onClose }) => {
  if (!plan) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f1f5f9' }}>
        <Icons.Description />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            実装プラン #{plan.id}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {plan.planned_date} ・ {plan.author}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* メタ情報 */}
        <Box sx={{ mb: 2 }}>
          <MetaRow label="達成目的" value={plan.goal} />
          <MetaRow
            label="参照リソース"
            value={
              plan.resources ? (
                plan.resources
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )
            }
          />
          <MetaRow
            label="対象ファイル"
            value={
              plan.file_paths && plan.file_paths.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {plan.file_paths.map((p, i) => (
                    <Chip key={i} label={p} size="small" variant="outlined" />
                  ))}
                </Box>
              ) : (
                '—'
              )
            }
          />
          <MetaRow label="AI会話ID" value={plan.conversation_id} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 計画詳細（JSONB の content をレンダリング） */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          計画詳細
        </Typography>
        <AiPlanBlockRenderer content={plan.content} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiPlanDetail;
